import { Autocomplete, Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material'
import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import ConversionSourceService from '../../../../../shared/services/conversion-source/conversion-source.service'
import { IConversionSource } from '../../../../../shared/services/conversion-source/conversion-source.model'
import SpendBasedConversionService from '../../../../../shared/services/spend-based-conversion/spend-based-conversion.service'
import { ISpendBasedConversion } from '../../../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import { formatConversionCodeLabel, sortByConversionCode } from '../../../../../shared/utils/conversion-utils'

/**
 * Spend factor picker: a conversion source select feeding an activity autocomplete.
 *
 * Writes `conversionSourceId` and `returnId` onto the formik it is given, so a caller submits those
 * two values as `{ conversionSourceId, spendBasedConversionId: returnId }`.
 *
 * `calendarYear` must come from the record being edited, not from today. The activity list is fetched
 * for that year (with the closest-active-year fallback), so passing the wrong year offers factors
 * priced for the wrong period.
 */
export const SpendCategoryPicker: FunctionComponent<{
  formik: any
  calendarYear: string
  disabled?: boolean
  sourceLabel?: string
  activityLabel?: string
}> = ({ formik, calendarYear, disabled = false, sourceLabel = 'Conversion Factor Source', activityLabel = 'Spend Activity' }) => {
  const conversionSourceService = useMemo(() => new ConversionSourceService(), [])
  const spendBasedConversionService = useMemo(() => new SpendBasedConversionService(), [])

  const [conversionSources, setConversionSources] = useState<IConversionSource[]>()
  const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>()

  const sortedSpendBasedConversions = useMemo(
    () => sortByConversionCode(spendBasedConversions),
    [spendBasedConversions],
  )

  useEffect(() => {
    // `active` is load bearing: the service builds `?active=…&conversionType=…`, so dropping it
    // leaves the type filter without a `?` and every source comes back.
    conversionSourceService.getAll(true, 'spendBasedConversion')
      .then(response => setConversionSources(response))
      .catch(err => console.log(err))
  }, [conversionSourceService])

  // Reload the activity list whenever the source or the year changes, and drop a selection that the
  // new list no longer offers.
  useEffect(() => {
    const sourceId = formik.values.conversionSourceId
    if (!sourceId || !calendarYear) {
      setSpendBasedConversions(undefined)
      return
    }
    let cancelled = false
    spendBasedConversionService.getSpendBasedConversionsForConversionSource(sourceId, calendarYear, true, true)
      .then(response => {
        if (cancelled) return
        setSpendBasedConversions(response)
        if (formik.values.returnId && !response?.some(conversion => conversion.id === formik.values.returnId)) {
          formik.setFieldValue('returnId', '')
        }
      })
      .catch(err => {
        if (!cancelled) console.log(err)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.conversionSourceId, calendarYear])

  const conversionSourceHandler = (e: SelectChangeEvent<string>) => {
    if (e.target.value === formik.values.conversionSourceId) return
    formik.setFieldValue('conversionSourceId', e.target.value)
    formik.setFieldValue('returnId', '')
  }

  return (
    <>
      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='conversionSourceId'>{sourceLabel}</InputLabel>
        <Select
          id='conversionSourceId' label={sourceLabel} name='conversionSourceId'
          value={formik.values.conversionSourceId || ''}
          onChange={conversionSourceHandler}
          disabled={disabled || !conversionSources}
          error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
          sx={{ color: 'text.primary' }}>
          {(conversionSources || []).slice().sort((a, b) => a.source.localeCompare(b.source)).map((source) =>
            <MenuItem key={source.id} value={source.id}>{source.source}</MenuItem>
          )}
        </Select>
        {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
            {formik.errors.conversionSourceId as string}
          </FormHelperText>
        }
      </FormControl>
      <FormControl fullWidth variant='outlined' sx={{ flexBasis: '200px', mt: 2 }}>
        <Autocomplete
          id='returnId'
          options={sortedSpendBasedConversions}
          freeSolo={false}
          autoHighlight
          disabled={disabled || !spendBasedConversions || spendBasedConversions.length === 0}
          getOptionLabel={(option) => typeof option === 'string' ? option : formatConversionCodeLabel(option)}
          renderOption={(props, option) => {
            const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
            return (
              <Box
                component='li'
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                key={key}
                {...restProps}
              >
                {formatConversionCodeLabel(option, ' -')}
              </Box>
            )
          }}
          value={spendBasedConversions?.find(conversion => conversion.id === formik.values.returnId) || null}
          onChange={(event, newValue) => {
            formik.setFieldValue('returnId', typeof newValue === 'string' ? '' : newValue?.id || '')
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ color: 'text.primary' }}
              label={activityLabel}
              error={formik.touched.returnId && Boolean(formik.errors.returnId)}
              helperText={formik.touched.returnId && formik.errors.returnId ? formik.errors.returnId as string : null}
            />
          )}
        />
      </FormControl>
    </>
  )
}
